import React, { useCallback, useEffect, useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Activity, ChevronDown, ChevronUp, Database, Loader2, RefreshCw } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const ms = (v) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(1)} s` : `${Math.round(v)} ms`);

const Verdict = ({ row }) => {
  const ops = row.avg_db_ops ?? row.max_db_ops ?? 0;
  if (ops >= 40) return <Badge className="bg-rose-600 text-white">too many DB round-trips</Badge>;
  if ((row.avg_db_ms ?? 0) > 0.6 * (row.avg_ms ?? row.p95_ms ?? 1)) return <Badge className="bg-amber-500 text-white">database time</Badge>;
  return <Badge variant="outline">server / external API</Badge>;
};

const RouteTable = ({ rows, kind, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-xs">
      <thead className="text-muted-foreground">
        <tr className="text-left">
          <th className="py-1 pr-2">Endpoint</th>
          <th className="py-1 pr-2">{kind === 'slow' ? 'Slow hits' : 'Calls'}</th>
          <th className="py-1 pr-2">{kind === 'slow' ? 'Avg' : 'p50'}</th>
          <th className="py-1 pr-2">{kind === 'slow' ? 'Max' : 'p95'}</th>
          <th className="py-1 pr-2">DB ops</th>
          <th className="py-1 pr-2">DB time</th>
          <th className="py-1">Cost is</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={7} className="py-2 text-muted-foreground">Nothing recorded yet.</td></tr>
        )}
        {rows.map((r) => (
          <tr key={r.route} className="border-t">
            <td className="py-1 pr-2 font-mono">{r.route}</td>
            <td className="py-1 pr-2">{r.count}</td>
            <td className="py-1 pr-2">{ms(kind === 'slow' ? r.avg_ms : r.p50_ms)}</td>
            <td className="py-1 pr-2 font-medium">{ms(kind === 'slow' ? r.max_ms : r.p95_ms)}</td>
            <td className="py-1 pr-2">{r.avg_db_ops} <span className="text-muted-foreground">(max {r.max_db_ops})</span></td>
            <td className="py-1 pr-2">{ms(r.avg_db_ms)}</td>
            <td className="py-1"><Verdict row={r} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SlowRequestsCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState(24);
  const [idx, setIdx] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async (h = hours) => {
    setBusy(true);
    try {
      const r = await api.get('/api/admin/ops/slow-requests', { params: { hours: h } });
      setData(r.data);
    } catch { toast.error('Could not load slow requests'); } finally { setBusy(false); }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  const ensureIndexes = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/admin/ops/ensure-indexes');
      setIdx(r.data);
      toast.success(`${r.data.ensured?.length || 0} indexes present${r.data.errors?.length ? `, ${r.data.errors.length} errors` : ''}`);
    } catch { toast.error('Index check failed'); } finally { setBusy(false); }
  };

  const slowCount = data?.slow_by_route?.length || 0;

  return (
    <div className="rounded-2xl border p-4" data-testid="slow-requests-card">
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="w-full flex flex-wrap items-center gap-2 text-left group"
              data-testid="slow-requests-toggle">
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Activity className="h-4 w-4 text-primary" />API latency
        </h3>
        {data && (
          <span className="flex flex-wrap items-center gap-2 text-xs" data-testid="slow-requests-summary">
            <Badge className={slowCount ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'}>
              {slowCount} slow endpoint{slowCount === 1 ? '' : 's'} · {hours} h
            </Badge>
            <Badge variant="outline" className="gap-1"><Database className="h-3 w-3" />DB round-trip {data.mongo_rtt_ms} ms</Badge>
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">
          {open ? 'Hide' : 'Details'}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>

      {!open && (
        <p className="text-xs text-muted-foreground mt-2">
          Server-side timing for every API response — open only when something feels slow.
        </p>
      )}

      {open && (
      <div className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-muted-foreground flex-1 min-w-[16rem]">
          Every API response is timed on the server; anything over {data ? (data.threshold_ms / 1000).toFixed(1) : '1.0'} s
          is stored for {data?.retention_days ?? 14} days. "DB ops" is the number of database round-trips one request made —
          on production each one costs the pod→DB distance, so 200 ops ≈ 6-7 s even when every query is fast.
        </p>
        <div className="ml-auto flex items-center gap-2">
          {[6, 24, 72, 168].map((h) => (
            <Button key={h} size="sm" variant={hours === h ? 'default' : 'outline'} className="h-7 text-xs"
                    data-testid={`slow-requests-window-${h}`}
                    onClick={() => { setHours(h); load(h); }}>{h < 48 ? `${h} h` : `${h / 24} d`}</Button>
          ))}
          <Button size="sm" variant="outline" className="h-7" onClick={() => load()} disabled={busy} data-testid="slow-requests-refresh-btn">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline">Connection pool {data.pool?.max_pool_size} (min {data.pool?.min_pool_size})</Badge>
        </div>
      )}

      <details className="space-y-1" open data-testid="slow-requests-slow-section">
        <summary className="text-sm font-medium cursor-pointer">Slow endpoints (stored, ≥ threshold)</summary>
        <RouteTable rows={data?.slow_by_route || []} kind="slow" testid="slow-requests-by-route" />
      </details>

      <details className="space-y-1" data-testid="slow-requests-live-section">
        <summary className="text-sm font-medium cursor-pointer">Live — every route in the last 60 min (this server)</summary>
        <RouteTable rows={data?.live_by_route || []} kind="live" testid="slow-requests-live" />
      </details>

      {data?.recent_slow?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer font-medium">Last {data.recent_slow.length} slow hits</summary>
          <div className="mt-2 max-h-64 overflow-y-auto space-y-1" data-testid="slow-requests-recent">
            {data.recent_slow.map((r, i) => (
              <div key={i} className="flex flex-wrap gap-x-3 border-t py-1">
                <span className="text-muted-foreground">{fmt(r.at)}</span>
                <span className="font-mono">{r.url}</span>
                <span className="font-medium">{ms(r.ms)}</span>
                <span>{r.db_ops} ops · db {ms(r.db_ms)}</span>
                <span className="text-muted-foreground">{r.role} · {r.client} · {r.status}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        <Button size="sm" variant="outline" onClick={ensureIndexes} disabled={busy} data-testid="ensure-indexes-btn">
          <Database className="h-3.5 w-3.5 mr-1" />Re-create hot-path indexes now
        </Button>
        <span className="text-xs text-muted-foreground">Runs automatically on every boot; use after a database migration.</span>
        {idx && (
          <span className="text-xs" data-testid="ensure-indexes-result">
            {idx.ensured?.length} present{idx.errors?.length ? ` · ${idx.errors.length} errors: ${idx.errors.join('; ')}` : ''}
          </span>
        )}
      </div>
      </div>
      )}
    </div>
  );
};
